import { useState, useContext, useEffect } from 'react'
import { Button } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'

import { toastErrorLog } from '../../helpers/toastErrorLog'
import { useAuth } from '../../hooks/useAuth'
import { useImagenesPublicacion } from '../../hooks/useImagenesPublicacion.js'
import { setModal } from '../../slices/modalSlice'
import { CarritoContext } from '../context/carritoContext'
import { calcularTotalItemConIva, calcularTotalItemSinIva } from '../helpers/calcularPrecioTotal'
import { useDeshabilitaCompra } from '../hooks/deshabilitaCompra.js'
import ComponenteError from '../publicaciones/ComponenteError'
import { GenericModal } from '../reutilizables/modal/GenericModal'
import Precios from '../reutilizables/precios/Precios'
import * as carritoService from '../servicios/carritoService'
import * as publicacionesService from '../servicios/publicacionesService'
import BordePrincipal from '../ui/BordePrincipal'
import styles from './Carrito.module.css'
import CarritoItem from './CarritoItem'

export const Carrito = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { loginDataContext: { idUsuario } = {} } = useAuth()
  const {
    setCantidad,
    items,
    setItems,
    setIdPresupuestoComprar,
    setIdPublicacionComprar,
    getImputacion
  } = useContext(CarritoContext)

  const [totalSuma, setTotalSuma] = useState(0)
  const [sumaTotal, setSumaTotal] = useState(0)
  const [itemsSeparados, setItemsSeparados] = useState([])
  const [centroDeCostos, setCentroDeCostos] = useState(null)
  const [tipoConfirmacion, setTipoConfirmacion] = useState(null)
  const [itemAEliminar, setItemAEliminar] = useState(null)
  const { idsDeshabilitados } = useDeshabilitaCompra(items)
  const esItemDeshabilitado = (item) => idsDeshabilitados.has(item.idPublicacion)

  const { adjuntos, loadingImagenes, fetchImagenes } = useImagenesPublicacion()

  const centroDeCostosLlamado = async () => {
    const resp = await getImputacion()
    const opcionesImputTpos = resp.opcionesImputTpos.map((element) => {
      return { value: element.tipoImpu, label: element.tipoImpuTextos }
    })
    const listaImputaciones = resp.listaImputaciones.map((element) => {
      return {
        codigo: element.activoFijo || element.codigo,
        descripcion: element.descripcion,
        tipoImpu: element.tipoImpu
      }
    })
    setCentroDeCostos({
      ...resp,
      opcionesImputTpos,
      listaImputaciones
    })
  }

  useEffect(() => {
    !centroDeCostos && centroDeCostosLlamado()
    sumarTotales()
  }, [totalSuma])

  const sumar = (valor) => {
    setTotalSuma(valor)
  }

  const completarDescuentos = async () => {
    if (!items || items.length === 0) {
      return
    }

    let huboCambios = false

    const itemsActualizados = await Promise.all(
      items.map(async (item) => {
        const tienePorcDcto = Number(item.porcDcto) > 0
        if (tienePorcDcto) {
          return item
        }

        const { data } = await publicacionesService.buscarDetalle(item.idPublicacion)
        const porcDcto = Number(data?.porcDcto ?? 0)

        if (porcDcto > 0) {
          huboCambios = true
          return {
            ...item,
            porcDcto
          }
        }

        return item
      })
    )

    if (huboCambios) {
      setItems(itemsActualizados)
    }
  }

  useEffect(() => {
    setIdPresupuestoComprar(0)
    setIdPublicacionComprar(0)
  }, [setIdPresupuestoComprar, setIdPublicacionComprar])

  useEffect(() => {
    completarDescuentos()
  }, [items])

  useEffect(() => {
    const itemsConDescuento = items

    setItemsSeparados(
      Object.values(
        itemsConDescuento.reduce((acc, item) => {
          const tieneDescuento = item.porcDcto > 0

          if (!acc[item.idVendedor]) {
            acc[item.idVendedor] = []
          }
          acc[item.idVendedor].push({
            ...item,
            tieneDescuento,
            valorTotalConIva: calcularTotalItemConIva(item),
            valorTotalSinIva: calcularTotalItemSinIva(item)
          })
          return acc
        }, {})
      )
    )
  }, [items])

  const handleCloseConfirm = () => {
    setTipoConfirmacion(null)
    setItemAEliminar(null)
  }

  const handleConfirmarBorradoItem = (id) => {
    openConfirm('item', id)
  }

  const confirmarBorrado = async () => {
    if (tipoConfirmacion === 'carrito') {
      await deleteCarritoCompleto()
    } else if (tipoConfirmacion === 'item' && itemAEliminar) {
      await deleteCarritoItem(itemAEliminar)
    }
    handleCloseConfirm()
    return true
  }

  const deleteCarritoItem = async (idpub) => {
    try {
      const item = items.find((item) => item.idPublicacion === idpub)

      const carritoEliminar = {
        idPublicacion: idpub,
        idUsuario
      }
      const { data: carritoCantidad } = await carritoService.eliminar(carritoEliminar)
      setSumaTotal(sumaTotal - calcularTotalItemConIva(item))
      const newItems = items.filter((item) => item.idPublicacion !== idpub)
      setItems(newItems)
      setCantidad(carritoCantidad)
    } catch (error) {
      const mensaje = `Ocurrió un error al obtener al eliminar el carrito.`
      toastErrorLog(error, mensaje, 'carrito')
    }
  }

  const deleteCarritoCompleto = async () => {
    try {
      for (const item of items) {
        const carritoEliminar = {
          idPublicacion: item.idPublicacion,
          idUsuario
        }
        await carritoService.eliminar(carritoEliminar)
      }

      setSumaTotal(0)
      setCantidad(0)
      setItems([])
    } catch (error) {
      const mensaje = `Ocurrió un error al eliminar el carrito.`
      toastErrorLog(error, mensaje, 'carrito')
    }
  }

  const sumarTotales = async () => {
    const totalItems = items.map((item) => calcularTotalItemConIva(item))
    const arrTotalesSinIVA = items.map((item) => calcularTotalItemSinIva(item))

    let total = 0
    let totalSinIva = 0
    for (let i = 0; i < totalItems.length; i++) {
      total = total + totalItems[i]
      totalSinIva = totalSinIva + arrTotalesSinIVA[i]
    }
    setSumaTotal(total)
  }

  const modificarCarrito = (valor, propiedad, item, codValor) => {
    const itemsModificados = items.map((i) =>
      i.idPublicacion === item ? { ...i, [propiedad]: valor, [`${propiedad}Cod`]: codValor } : i
    )
    setItems(itemsModificados)
  }

  const copiar = (propiedad, valorAReplicar) => {
    setItems((prev) =>
      prev.map((i) => (i[propiedad] === valorAReplicar ? i : { ...i, [propiedad]: valorAReplicar }))
    )
  }

  const irAPublicacionesFiltrandoPorVendedor = (grupo) => {
    const { idVendedor, razonSocial } = grupo[0]
    navigate('/Publicaciones', {
      state: { idVendedorCarrito: idVendedor, razonSocial }
    })
  }

  const openConfirm = (tipo, idItem = null) => {
    setTipoConfirmacion(tipo)
    setItemAEliminar(idItem)

    const body =
      tipo === 'carrito'
        ? '¿Estás seguro de que querés eliminar todo el carrito?'
        : '¿Estás seguro de que querés eliminar esta publicación del carrito?'

    dispatch(
      setModal({
        size: 'sm',
        title: 'Confirmar eliminación',
        type: 'botones',
        body: body,
        show: true
      })
    )
  }
  const hayItemsSinStock = items.some(
    (item) => item.cantDisponible !== null && item.cantDisponible < item.cantidad
  )

  useEffect(() => {
    if (items.length > 0) {
      fetchImagenes({
        idPublicaciones: items.map((item) => item.idPublicacion)
      })
    }
  }, [items])

  return (
    <>
      <div className="row">
        <div className={`col-12 col-lg-9 mb-3 ${styles.carrito}`}>
          <BordePrincipal titulo="Mi Carrito">
            {items.length > 0 && (
              <div className={styles.posicionBoton}>
                <Button
                  variant="light"
                  className={styles.btnBackground}
                  onClick={() => openConfirm('carrito')}
                >
                  <i className={`bi bi-trash-fill ${styles.btnIconColor}`}></i>
                </Button>
              </div>
            )}
            <div className={`${styles.botonWidth} ${styles.posicionVolverPublicaciones}`}>
              <Link to="/Publicaciones/">Ver más publicaciones</Link>
            </div>
            <div className={`mb-5 ${styles.marginTopTitulo} ${styles.gapCarrito}`}>
              {itemsSeparados.map((grupo, indexGrupo) => (
                <div key={indexGrupo} className={styles.grupoCarrito}>
                  <div
                    className={`d-flex justify-content-between align-items-center ${styles.grupoTitulo} mb-3`}
                  >
                    <div className={styles.vendedorContenedor}>
                      <span className={styles.vendedorLabel}>Vendedor:</span>
                      <button
                        className={styles.buttonPublicaciones}
                        title="Ver más publicaciones de este proveedor"
                        onClick={() => irAPublicacionesFiltrandoPorVendedor(grupo)}
                      >
                        <h5 className={styles.tituloVendedor}>{grupo[0].razonSocial}</h5>
                      </button>
                    </div>
                    <div className="d-flex flex-column align-items-center">
                      <Precios
                        nombre={'Subtotal'}
                        items={grupo}
                        showSinIVA={true}
                        monedaSimbolo={'$'}
                      />
                    </div>
                  </div>
                  {grupo.map((item) => (
                    <CarritoItem
                      key={item.idPublicacion}
                      item={item}
                      onDelete={handleConfirmarBorradoItem}
                      sumar={sumar}
                      totalSuma={totalSuma}
                      sumarTotales={sumarTotales}
                      centroDeCostos={centroDeCostos}
                      modificarCarrito={modificarCarrito}
                      copiar={copiar}
                      setItems={setItems}
                      items={items}
                      adjunto={adjuntos.find((a) => a.idPublicacion === item.idPublicacion)}
                      loadingImagenes={loadingImagenes}
                    />
                  ))}
                </div>
              ))}
            </div>
            <div className="d-flex justify-content-center align-items-center gap-3">
              {items.length === 0 && <ComponenteError tipo="I" idAviso={4} />}
            </div>
          </BordePrincipal>
        </div>
        <div className={`col-12 col-lg-3 ${styles.resumenCompra}`}>
          {items.length > 0 && (
            <BordePrincipal>
              <div className={styles.resumenContenido}>
                <h4 className={styles.resumenTitulo}>Resumen de compra</h4>
                <div className={styles.resumenTotal}>
                  <Precios
                    nombre={'Total'}
                    items={itemsSeparados.flat()}
                    showSinIVA={true}
                    monedaSimbolo={'$'}
                  />
                </div>

                <div className={styles.resumenAccion}>
                  {hayItemsSinStock ? (
                    <p className={styles.sinStock}>Sin Stock</p>
                  ) : (
                    <button
                      className={`btn btn-volver-comprar ${styles.botonContinuar}`}
                      type="button"
                      disabled={items.every((item) => esItemDeshabilitado(item))}
                      onClick={() => navigate('/Compras')}
                    >
                      Continuar compra
                    </button>
                  )}
                </div>
              </div>
            </BordePrincipal>
          )}
        </div>
      </div>

      <GenericModal onClose={handleCloseConfirm} onConfirm={confirmarBorrado} />
    </>
  )
}

export default Carrito
